import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const FRESHDESK_SCRIPT_ID = 'calensync-freshdesk-chat';
const SCRIPT_SRC = 'https://eu.fw-cdn.com/13496294/1387426.js';
const STACKING_STYLE_ID = 'calensync-freshdesk-stacking';
const FRESHCHAT_FRAME_IDS = ['fc_frame', 'fc_push_frame', 'launcher-frame', 'freshworks-frame'];
/** Below Mantine modal/drawer overlays (200) so dashboard dialogs stay clickable. */
const FRESHCHAT_Z_INDEX = '100';

function pinFreshchatBelowOverlays() {
  for (const id of FRESHCHAT_FRAME_IDS) {
    const el = document.getElementById(id);
    if (!el) {
      continue;
    }
    if (el.style.zIndex === FRESHCHAT_Z_INDEX && el.style.getPropertyPriority('z-index') === 'important') {
      continue;
    }
    el.style.setProperty('z-index', FRESHCHAT_Z_INDEX, 'important');
  }
}

function ensureFreshchatStackingStyle() {
  if (document.getElementById(STACKING_STYLE_ID)) {
    return;
  }
  const style = document.createElement('style');
  style.id = STACKING_STYLE_ID;
  style.textContent = FRESHCHAT_FRAME_IDS.map((id) => `#${id}`).join(',\n')
    + ` {\n  z-index: ${FRESHCHAT_Z_INDEX} !important;\n}\n`;
  document.head.appendChild(style);
}

/**
 * Freshdesk / Freshchat widget for the dashboard route only.
 * Mount this component on Dashboard; unmount tears the widget down so it does not follow SPA navigations.
 */
export function FreshdeskChat({ hidden = false }: { hidden?: boolean }) {
  const { user } = useAuth();
  const [scriptAppended, setScriptAppended] = useState(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    ensureFreshchatStackingStyle();

    const frameObservers: MutationObserver[] = [];
    const watchedFrames = new Set<Element>();
    const watchNewFrames = () => {
      pinFreshchatBelowOverlays();
      for (const id of FRESHCHAT_FRAME_IDS) {
        const el = document.getElementById(id);
        if (!el || watchedFrames.has(el)) {
          continue;
        }
        watchedFrames.add(el);
        const frameObserver = new MutationObserver(pinFreshchatBelowOverlays);
        frameObserver.observe(el, { attributes: true, attributeFilter: ['style', 'class'] });
        frameObservers.push(frameObserver);
      }
    };

    watchNewFrames();
    const bodyObserver = new MutationObserver(watchNewFrames);
    bodyObserver.observe(document.body, { childList: true, subtree: true });

    let existing = document.getElementById(FRESHDESK_SCRIPT_ID) as HTMLScriptElement | null;
    if (!existing) {
      const script = document.createElement('script');
      script.id = FRESHDESK_SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      script.setAttribute('chat', 'true');
      script.onload = () => {
        if (!cancelledRef.current) {
          watchNewFrames();
          setScriptAppended(true);
        }
      };
      document.head.appendChild(script);
    } else {
      setScriptAppended(true);
    }

    return () => {
      cancelledRef.current = true;
      bodyObserver.disconnect();
      for (const observer of frameObservers) {
        observer.disconnect();
      }
      document.getElementById(STACKING_STYLE_ID)?.remove();
      try {
        window.fcWidget?.destroy?.();
      } catch {
        /* Freshdesk may throw if partially initialized */
      }
      document.getElementById(FRESHDESK_SCRIPT_ID)?.remove();
    };
  }, []);

  useEffect(() => {
    const uuid = user?.uuid;
    if (!uuid || !scriptAppended) {
      return;
    }

    const email = user?.email?.trim() || null;

    let intervalId: number | undefined;
    let attempts = 0;
    const maxAttempts = 150;

    const tryIdentifyUser = () => {
      const fc = window.fcWidget;
      if (!fc?.setExternalId) {
        return false;
      }
      fc.setExternalId(uuid);
      if (email && fc.user?.setEmail) {
        try {
          fc.user.setEmail(email);
        } catch {
          /* widget may reject email before user record exists */
        }
      }
      return true;
    };

    if (tryIdentifyUser()) {
      return;
    }

    intervalId = window.setInterval(() => {
      attempts += 1;
      if (tryIdentifyUser() || attempts >= maxAttempts) {
        if (intervalId !== undefined) {
          window.clearInterval(intervalId);
        }
      }
    }, 200);

    return () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, [user?.uuid, user?.email, scriptAppended]);

  useEffect(() => {
    if (!scriptAppended) {
      return;
    }

    let intervalId: number | undefined;
    let attempts = 0;
    const maxAttempts = 150;

    const applyVisibility = () => {
      const fc = window.fcWidget;
      if (!fc?.hide || !fc?.show) {
        return false;
      }
      if (hidden) {
        fc.hide();
      } else {
        fc.show();
      }
      return true;
    };

    if (applyVisibility()) {
      return;
    }

    intervalId = window.setInterval(() => {
      attempts += 1;
      if (applyVisibility() || attempts >= maxAttempts) {
        if (intervalId !== undefined) {
          window.clearInterval(intervalId);
        }
      }
    }, 200);

    return () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, [hidden, scriptAppended]);

  return null;
}
